import React, { useState, useEffect, useMemo, useDeferredValue } from "react"
import { useNavigate } from "react-router-dom"
import styles from "../../asset/styles/excursions/excursions.module.scss"
import Table, { TableColumn } from "../../component/table/table"
import utilStyles from "../../asset/styles/util/util.module.scss"
import IconsColor from "util/iconsColor"
import { agent } from "apiClient/api"
import { ExcursionData, ExcursionPricingModel, ExcursionDetailAddOnModel } from "../../models"
import moment from "moment"
import CustomizedMenu from "component/muiMenu/muiMenu"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import { useSnackbar } from "../../context/snackbarContext"
import Loader from "../../component/loader/loaderComponent"
import Popup from "../../component/popup/popup"
import MainTabsTableHeader, { FilterGroup } from "../../component/mainTabsTableHeader/mainTabsTableHeader"
import { applyFilters, applySearch, cycleSort, sortRowsBy, uniqueOptionsFromRows } from "../../util/listFilters"
import { useListView, LIST_VIEW_KEYS } from "../../context/listFilterContext"
import { toTitleCase } from "util/textcase"
import { TablePaginationBar, useClientListPagination } from "../../component/tablePagination"

const Excursions: React.FC = () => {
    const navigate = useNavigate()
    const { showError, showSuccess } = useSnackbar()
    const [excursions, setExcursions] = useState<ExcursionData[]>([])
    const {
        search: searchValue, setSearch: setSearchValue,
        sortState, setSortState,
        filters: tableSelectedFilters, setFilters: setTableSelectedFilters,
        page, setPage, pageSize, setPageSize,
    } = useListView(LIST_VIEW_KEYS.excursions)
    const handleColumnSort = React.useCallback(
        (key: string) => setSortState((prev) => cycleSort(prev, key)),
        [setSortState],
    )
    const [isConfirmationPopupOpen, setIsConfirmationPopupOpen] = useState(false)
    const [confirmationType, setConfirmationType] = useState<'status' | 'delete' | null>(null)
    const [selectedExcursionForAction, setSelectedExcursionForAction] = useState<ExcursionData | null>(null)
    const [excursionUsageData, setExcursionUsageData] = useState<{ day_trip_name: string }[]>([])
    const [excursionUsageLoading, setExcursionUsageLoading] = useState(false)
    const [isAddOnsPopupOpen, setIsAddOnsPopupOpen] = useState(false)
    const [selectedExcursionForAddOns, setSelectedExcursionForAddOns] = useState<ExcursionData | null>(null)
    const { setHotelGenericData } = useHotelGeneric()

    const openAddOnsPopup = (excursion: ExcursionData) => {
        setSelectedExcursionForAddOns(excursion)
        setIsAddOnsPopupOpen(true)
    }

    const closeAddOnsPopup = () => {
        setIsAddOnsPopupOpen(false)
        setSelectedExcursionForAddOns(null)
    }

    const GetExcursionList = async () => {
        try {
            const response = await agent.ExcursionsListAPI.get()
            if (response?.status === true) {
                setExcursions(response?.data)
            }
        } catch (error) {
            console.log("GetExcursionList Error", error)
        }
    }

    const GetGenericHotelDetails = async () => {
        try {
            const response = await agent.GenericGlobalAPI.get()
            if (response?.status === false) {
                console.log("Failed to fetch generic hotel details", response?.error)
            } else {
                setHotelGenericData(response?.data[0])
            }
        } catch (error) {
            console.log("GetGenericHotelDetails Error", error)
        }
    }

    useEffect(() => {
        (async () => {
            await Promise.all([
                GetExcursionList(),
                GetGenericHotelDetails()
            ])
        })()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleDeleteClick = (excursion: ExcursionData) => {
        setSelectedExcursionForAction(excursion)
        setConfirmationType("delete")
        setExcursionUsageLoading(true)
        agent.CheckExcursionUsageAPI.post({ excursion_id: excursion.excursion_id }, true)
            .then((response) => {
                if (response?.status === true && Array.isArray(response?.data)) {
                    setExcursionUsageData(response.data)
                } else {
                    setExcursionUsageData([])
                }
            })
            .catch(() => setExcursionUsageData([]))
            .finally(() => {
                setExcursionUsageLoading(false)
                setIsConfirmationPopupOpen(true)
            })
    }

    const handleConfirmStatusChange = async () => {
        if (!selectedExcursionForAction) return
        try {
            setIsConfirmationPopupOpen(false)
            const response = await agent.DeleteExcursionAPI.put({
                excursion_id: selectedExcursionForAction.excursion_id,
                flag_active: !selectedExcursionForAction.flag_active,
                excursion_flag_deleted: false,
            })
            if (response?.status === false) {
                showError(response?.error?.message || "Failed to update excursion status")
            } else {
                showSuccess("Excursion status updated successfully")
                setSelectedExcursionForAction(null)
                setConfirmationType(null)
                GetExcursionList()
            }
        } catch (error) {
            showError("Failed to update excursion status")
        }
    }

    const handleConfirmDelete = async () => {
        if (!selectedExcursionForAction) return
        try {
            setIsConfirmationPopupOpen(false)
            const response = await agent.DeleteExcursionAPI.put({
                excursion_id: selectedExcursionForAction.excursion_id,
                flag_active: selectedExcursionForAction.flag_active,
                excursion_flag_deleted: true,
            })
            if (response?.status === false) {
                showError(response?.error?.message || "Failed to delete excursion")
            } else {
                showSuccess("Excursion deleted successfully")
                setSelectedExcursionForAction(null)
                setConfirmationType(null)
                GetExcursionList()
            }
        } catch (error) {
            showError("Failed to delete excursion")
        }
    }

    const handleCancelConfirmation = () => {
        setIsConfirmationPopupOpen(false)
        setSelectedExcursionForAction(null)
        setConfirmationType(null)
        setExcursionUsageData([])
    }

    const getAddOnPriceByCategory = (addOn: ExcursionDetailAddOnModel, categoryName: string): number => {
        const p = addOn.add_on_pricing?.find(
            (x) => String(x.category_name).toLowerCase() === String(categoryName).toLowerCase()
        )
        return p != null ? (p.final_price ?? p.price) : 0
    }

    /** Unique category names from all add_on_pricing, ordered by category_id */
    const getAddOnPriceCategories = (excursion: ExcursionData): { category_id: number; category_name: string }[] => {
        const addOns = excursion.excursion_add_ons ?? []
        const map = new Map<number, string>()
        addOns.forEach((addOn) => {
            addOn.add_on_pricing?.forEach((p) => {
                if (p.category_id != null && p.category_name != null) {
                    map.set(p.category_id, p.category_name)
                }
            })
        })
        return Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([category_id, category_name]) => ({ category_id, category_name }))
    }

    const formatCategoryLabel = (name: string): string => {
        if (!name) return name
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
    }

    const columns: TableColumn<ExcursionData>[] = [
        {
            key: "excursion_name",
            label: "Excursion Name",
            width: "15%",
            align: "left",
            render: (value: string, row: ExcursionData) => (
                <div className={styles.excursionNameCell}>
                    <span
                        className={styles.excursionNameLink}
                        title={toTitleCase(value)}
                        onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/excursions/${row.excursion_id}`)
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault()
                                navigate(`/excursions/${row.excursion_id}`)
                            }
                        }}
                    >
                        {toTitleCase(value)}
                    </span>
                </div>
            ),
        },
        {
            key: "city_name",
            label: "City",
            width: "10%",
            align: "left",
            render: (value: string) => (
                <span className={styles.locationCell}>
                    <IconsColor iconName="location" width="16" height="16" fillColor="none" strokeColor="#7a7a7a" />
                    {toTitleCase(value)}
                </span>
            ),
        },
        // {
        //     key: "excursion_type_name",
        //     label: "Type",
        //     width: "10%",
        //     align: "left",
        // },
        {
            key: "excursion_pricing",
            label: "Price",
            width: "15%",
            align: "left",
            render: (value: ExcursionPricingModel[]) => (
                <div className={styles.priceCell}>
                    {value.map((item: ExcursionPricingModel) => {
                        return (
                            <span>
                                {toTitleCase(item.category_name)} : {item.base_price}
                            </span>
                        )
                    })}
                </div>
            ),
        },
        {
            key: "excursion_add_on_count",
            label: "Add-Ons",
            width: "10%",
            align: "center",
            render: (value: number, row: ExcursionData) => {
                const hasAddOns = (value != null && value > 0) || (row.excursion_add_ons?.length ?? 0) > 0
                return (
                    <span
                        role="button"
                        tabIndex={0}
                        className={hasAddOns ? styles.addOnsLink : undefined}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (hasAddOns) openAddOnsPopup(row)
                        }}
                        onKeyDown={(e) => {
                            if (hasAddOns && (e.key === "Enter" || e.key === " ")) {
                                e.preventDefault()
                                openAddOnsPopup(row)
                            }
                        }}
                    >
                        {value ?? row.excursion_add_ons?.length ?? 0}
                    </span>
                )
            },
        },
        {
            key: "flag_active",
            label: "Status",
            width: "5%",
            align: "center",
            render: (value: boolean) => <span className={value === true ? utilStyles.statusBadgeActive : value === false ? utilStyles.statusBadgeInactive : utilStyles.statusBadgePending}>{value ? "Active" : "Inactive"}</span>,
        },
        {
            key: "updated_at_timestamp",
            label: "Last Updated",
            width: "14%",
            align: "left",
            render: (value: string) => `${moment(value).format("DD-MMM-YYYY hh:mm A")}`,
        },
        {
            key: "updated_by",
            label: "Editor",
            width: "10%",
            align: "left",
        },
        {
            key: "flag_active",
            label: "",
            width: "5%",
            align: "center",
            render: (value: boolean, row: ExcursionData) => (
                <CustomizedMenu
                    buttonText=""
                    menuItems={[
                        {
                            label: `Set Status to ${value ? "Inactive" : "Active"}`,
                            onClick: () => {
                                setSelectedExcursionForAction(row)
                                setConfirmationType("status")
                                setIsConfirmationPopupOpen(true)
                            },
                        },
                        {
                            label: `Delete Excursion`,
                            onClick: () => handleDeleteClick(row),
                        },
                    ]}
                    buttonVariant="text"
                    buttonStyle={{ padding: "0px", minWidth: "0px" }}
                    buttonEndIcon={<IconsColor iconName="moreVert" width="20" height="20" fillColor="none" strokeColor="#7a7a7a" />}
                />
            ),
        },
    ]

    // ----- Filter accessors (City, Editor, Status per spec) -----
    const FILTER_ACCESSORS = useMemo(
        () => ({
            city: (row: ExcursionData) => row.city_name,
            editor: (row: ExcursionData) => String((row as any).updated_by ?? ''),
            status: (row: ExcursionData) => (row.flag_active ? 'Active' : 'Inactive'),
        }),
        [],
    )

    const filterGroups: FilterGroup[] = useMemo(
        () => [
            { key: 'city', label: 'City', options: uniqueOptionsFromRows(excursions, FILTER_ACCESSORS.city) },
            { key: 'editor', label: 'Editor', options: uniqueOptionsFromRows(excursions, FILTER_ACCESSORS.editor) },
            {
                key: 'status',
                label: 'Status',
                options: [
                    { label: 'Active', value: 'Active' },
                    { label: 'Inactive', value: 'Inactive' },
                ],
            },
        ],
        [excursions, FILTER_ACCESSORS],
    )

    // Per-column sort overrides. The Price column shows multi-category pricing (Adult + Child +
    // Senior etc.) — sorting by the raw array would be meaningless, so we extract the Adult
    // base_price for comparison instead.
    const SORT_ACCESSORS = useMemo(
        () => ({
            excursion_pricing: (r: ExcursionData) => {
                const adult = (r.excursion_pricing || []).find((p) => /adult/i.test(p.category_name || ""))
                return Number(adult?.base_price ?? 0)
            },
        }),
        [],
    )

    // Defer heavy table re-render so clearing search/filters doesn't block the UI.
    const deferredSearch = useDeferredValue(searchValue)
    const deferredFilters = useDeferredValue(tableSelectedFilters)
    const filteredExcursions = useMemo(() => {
        let rows = applyFilters(excursions, deferredFilters, FILTER_ACCESSORS)
        rows = applySearch(rows, deferredSearch, [(r) => r.excursion_name, (r) => r.city_name])
        rows = sortRowsBy(rows, sortState, SORT_ACCESSORS)
        return rows
    }, [excursions, deferredFilters, deferredSearch, sortState, FILTER_ACCESSORS, SORT_ACCESSORS])

    const paginationQueryKey = JSON.stringify({ search: searchValue, filters: tableSelectedFilters, sortState })
    const {
        pagedRows: pagedExcursions,
        total: pagedTotal,
        page: currentPage,
        pageSize: currentPageSize,
        handlePageChange,
        handlePageSizeChange,
    } = useClientListPagination(filteredExcursions, {
        page, pageSize, setPage, setPageSize, queryKey: paginationQueryKey,
    })

    // const handleRowClick = (row: ExcursionData) => {
    //     navigate(`/excursions/${row.excursion_id}`)
    // }

    return (
        <>
            {excursionUsageLoading && <Loader message="Checking usage..." overlay={true} />}
            <Popup
                isOpen={isConfirmationPopupOpen}
                onClose={handleCancelConfirmation}
                title={confirmationType === "status" ? "Change Excursion Status" : "Delete Excursion"}
                primaryButton={{
                    label: "Confirm",
                    onClick: confirmationType === "status" ? handleConfirmStatusChange : handleConfirmDelete,
                }}
                secondaryButton={{
                    label: "Cancel",
                    onClick: handleCancelConfirmation,
                }}
                heightFiexd={true}
            >
                <div className={styles.confirmationContent}>
                    <IconsColor iconName="warningSign" width="80px" height="80px" fillColor="none" strokeColor="#FF0000" />
                    {confirmationType === "status" && selectedExcursionForAction && (
                        <p className={styles.confirmationText}>
                            Are you sure you want to change the status of <strong>{selectedExcursionForAction.excursion_name}</strong> to{" "}
                            <strong>{selectedExcursionForAction.flag_active ? "Inactive" : "Active"}</strong>?
                        </p>
                    )}
                    {confirmationType === "delete" && selectedExcursionForAction && (
                        <>
                            <p className={styles.confirmationText}>
                                Are you sure you want to delete <strong>{selectedExcursionForAction.excursion_name}</strong>?
                            </p>
                            {excursionUsageData.length > 0 ? (
                                <div className={styles.excursionUsageWarning}>
                                    <p className={styles.excursionUsageWarningTitle}>
                                        Listed places are going to Inactive on Delete:
                                    </p>
                                    <ul className={styles.excursionUsageList}>
                                        {excursionUsageData.map((item, index) => (
                                            <li key={index}>{item.day_trip_name}</li>
                                        ))}
                                    </ul>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </Popup>
            <Popup
                isOpen={isAddOnsPopupOpen}
                onClose={closeAddOnsPopup}
                title="Add-ons"
                size="medium"
                showCloseButton={true}
                heightFiexd={false}
            >
                {selectedExcursionForAddOns && (() => {
                    const priceCategories = getAddOnPriceCategories(selectedExcursionForAddOns)
                    const basePriceParts = selectedExcursionForAddOns.excursion_pricing?.map(
                        (p) => `${formatCategoryLabel(p.category_name)} ${p.base_price}`
                    ) ?? []
                    return (
                        <div className={styles.addOnsPopupContent}>
                            <p className={styles.addOnsPopupSubtitle}>{selectedExcursionForAddOns.excursion_name}</p>
                            <div className={styles.addOnsTableWrap}>
                                <table className={styles.addOnsTable}>
                                    <thead>
                                        <tr>
                                            <th>Add-on</th>
                                            {priceCategories.map((c) => (
                                                <th key={c.category_id}>{formatCategoryLabel(c.category_name)} Price</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(selectedExcursionForAddOns.excursion_add_ons ?? []).map((addOn) => (
                                            <tr key={addOn.excursion_add_on_id}>
                                                <td>{addOn.add_on_name}</td>
                                                {priceCategories.map((c) => (
                                                    <td key={c.category_id}>{getAddOnPriceByCategory(addOn, c.category_name)}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {basePriceParts.length > 0 && (
                                <div className={styles.addOnsFooter}>
                                    Base Price: {basePriceParts.join(" | ")}
                                </div>
                            )}
                        </div>
                    )
                })()}
            </Popup>
            <div className={`${utilStyles.pageContainer} ${utilStyles.listPageFill}`}>
            <div className={utilStyles.listPageHeader}>
                    <MainTabsTableHeader
                        searchValue={searchValue}
                        onSearchChange={setSearchValue}
                        searchPlaceholder="Search excursions"
                        totalCount={excursions.length}
                        filteredCount={filteredExcursions.length}
                        itemLabel="excursions"
                        sortValue=""
                        onSortChange={() => undefined}
                        sortOptions={[]}
                        filterGroups={filterGroups}
                        selectedFilters={tableSelectedFilters}
                        onFiltersChange={setTableSelectedFilters}
                        addButtonLabel="Excursion"
                        onAddClick={() => navigate("/excursions/new")}
                        showResultCount={false}
                    />
            </div>
            <div className={utilStyles.listPageTableBlock}>
                <div className={utilStyles.listPageTableScroll}>
                    <Table columns={columns} data={pagedExcursions} keyExtractor={(row: ExcursionData) => row.excursion_id} emptyMessage="No excursions available" minWidth={1000} fillHeight={true} sortKey={sortState.key} sortDirection={sortState.direction} onSort={handleColumnSort} />
                </div>
                <TablePaginationBar
                    total={pagedTotal}
                    page={currentPage}
                    pageSize={currentPageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>
        </div>
        </>
    )
}

export default Excursions
